import { useMediaQuery } from '@cocoonspace/shared/hooks/use-media-query.hook'
import type { ComponentProps, PropsWithChildren, ReactNode } from 'react'

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '~/components/ui/alert-dialog'
import { Button } from '~/components/ui/button'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '~/components/ui/drawer'

export const AlertDialogOrDrawer = ({
	trigger,
	children,
	title,
	cancelLabel,
	confirmLabel,
	onConfirm,
	dialogProps,
	drawerProps,
}: PropsWithChildren<{
	trigger: ReactNode
	title: ReactNode
	cancelLabel: string
	confirmLabel: string
	onConfirm: VoidFunction
	dialogProps?: ComponentProps<typeof AlertDialog>
	drawerProps?: ComponentProps<typeof Drawer>
}>) => {
	const isDesktop = useMediaQuery('(min-width: 768px)')

	if (isDesktop) {
		return (
			<AlertDialog {...dialogProps}>
				<AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>

				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{title}</AlertDialogTitle>
					</AlertDialogHeader>

					<AlertDialogDescription>{children}</AlertDialogDescription>

					<AlertDialogFooter>
						<AlertDialogCancel>{cancelLabel}</AlertDialogCancel>

						<AlertDialogAction onClick={onConfirm}>
							{confirmLabel}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		)
	}

	return (
		<Drawer {...drawerProps}>
			<DrawerTrigger asChild>{trigger}</DrawerTrigger>

			<DrawerContent>
				<DrawerHeader className='text-left'>
					<DrawerTitle>{title}</DrawerTitle>
				</DrawerHeader>

				<DrawerDescription className='mb-4 px-4'>{children}</DrawerDescription>

				<DrawerFooter className='flex flex-col items-center gap-6 pt-2'>
					<DrawerClose asChild>
						<Button
							className='w-full'
							onClick={onConfirm}
						>
							{confirmLabel}
						</Button>
					</DrawerClose>

					<DrawerClose asChild>
						<Button variant='link'>{cancelLabel}</Button>
					</DrawerClose>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}
